import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import TermsAcceptancePopup from './TermsAcceptancePopup';
import TrialCreditPopup from './TrialCreditPopup';
import { getMe } from '../api';

// Responsive breakpoint
function useIsMobile(bp = 768) {
    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < bp : false);
    useEffect(() => {
        const h = () => setIsMobile(window.innerWidth < bp);
        window.addEventListener('resize', h);
        return () => window.removeEventListener('resize', h);
    }, [bp]);
    return isMobile;
}


const s = {
    app:     { display: 'flex', minHeight: '100vh', background: '#f0f2f5' },
    sidebar: { width: 224, background: '#0f1117', display: 'flex', flexDirection: 'column', flexShrink: 0, transition: 'transform .25s ease', position: 'sticky', top: 0, height: '100vh', overflow: 'hidden' },
    logo:    { padding: '20px 18px', display: 'flex', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.06)' },
    logoTxt: { color: '#f1f5f9', fontWeight: 800, fontSize: 17, letterSpacing: '-0.3px' },
    nav:     { flex: 1, padding: '10px 0', overflowY: 'auto' },
    link:    { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 18px', color: '#64748b', textDecoration: 'none', fontSize: 13.5, transition: 'background .15s, color .15s', borderLeft: '3px solid transparent' },
    active:  { color: '#f1f5f9', background: 'rgba(29,78,216,0.12)', borderLeft: '3px solid #1d4ed8' },
    section: { padding: '18px 18px 6px', fontSize: 10, color: '#374151', textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 700 },
    bottom:  { borderTop: '1px solid rgba(255,255,255,0.06)', padding: '14px 18px' },
    username:{ color: '#94a3b8', fontSize: 12, marginBottom: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    logout:  { width: '100%', background: 'none', border: '1px solid rgba(255,255,255,0.1)', color: '#64748b', padding: '8px 0', borderRadius: 8, cursor: 'pointer', fontSize: 13, transition: 'all .2s' },
    main:    { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 },
    header:  { background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '13px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 },
    hLeft:   { display: 'flex', alignItems: 'center', gap: 12 },
    hTitle:  { fontWeight: 600, fontSize: 15, color: '#1a1d23' },
    hBal:    { fontSize: 13, color: '#1d4ed8', fontWeight: 700, background: '#ede9fe', padding: '4px 12px', borderRadius: 20 },
    content: { flex: 1, overflow: 'auto', padding: 24 },
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 99, display: 'none' },
    ham:     { background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'none', flexDirection: 'column', gap: 5 },
    hamLine: { display: 'block', width: 22, height: 2, background: '#6b7280', borderRadius: 2 },
};

const navStyle = (isActive) => ({
    ...s.link,
    ...(isActive ? s.active : {}),
    borderLeft: isActive ? '3px solid #1d4ed8' : '3px solid transparent',
});

export default function Layout() {
    const navigate  = useNavigate();
    const isMobile  = useIsMobile();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const location = useLocation();
    const [balance, setBalance] = useState(() => {
        try { return JSON.parse(localStorage.getItem('client'))?.balance ?? '—'; } catch { return '—'; }
    });
    const contentRef = useRef(null);
    const [showBackToTop, setShowBackToTop] = useState(false);

    useEffect(() => {
        const el = contentRef.current;
        if (!el) return;
        const onScroll = () => setShowBackToTop(el.scrollTop > 300);
        el.addEventListener('scroll', onScroll, { passive: true });
        return () => el.removeEventListener('scroll', onScroll);
    }, []);

    // Refresh balance from API on route change + every 60s
    const refreshBalance = useCallback(() => {
        getMe().then(res => {
            const bal = res.data?.balance;
            if (bal !== undefined) {
                setBalance(bal);
                try {
                    const c = JSON.parse(localStorage.getItem('client') || '{}');
                    localStorage.setItem('client', JSON.stringify({ ...c, balance: bal }));
                } catch {}
            }
        }).catch(() => {});
    }, []);

    useEffect(() => { refreshBalance(); }, [location.pathname, refreshBalance]);
    useEffect(() => {
        const iv = setInterval(refreshBalance, 60000);
        return () => clearInterval(iv);
    }, [refreshBalance]);

    const [termsAccepted, setTermsAccepted] = useState(() => {
        try { return !!JSON.parse(localStorage.getItem('client'))?.terms_accepted_at; } catch { return true; }
    });

    const [showTrialPopup, setShowTrialPopup] = useState(() => {
        try {
            if (localStorage.getItem('trial_popup_shown')) return false;
            const c = JSON.parse(localStorage.getItem('client'));
            return c && parseFloat(c.trial_credit) > 0 && new Date(c.trial_expires_at) > new Date();
        } catch { return false; }
    });

    // 2026-07-11: avviso IG update + refund riattivato (grande aggiornamento Instagram, azioni bloccate)
    // 2026-08-04: problema IG RISOLTO → avviso spento (default false).
    const [showCsNotice, setShowCsNotice] = useState(false);
    const dismissCsNotice = () => { setShowCsNotice(false); };

    const client = (() => { try { return JSON.parse(localStorage.getItem('client')); } catch { return {}; } })();
    const isAdmin = !!client?.is_admin;

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('client');
        navigate('/login');
    };

    const closeSidebar = () => setSidebarOpen(false);

    const sidebarStyle = {
        ...s.sidebar,
        ...(isMobile ? {
            position: 'fixed', top: 0, left: 0, height: '100%', zIndex: 100,
            transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
        } : {}),
    };

    const overlayStyle = {
        ...s.overlay,
        display: isMobile && sidebarOpen ? 'block' : 'none',
    };

    const hamStyle = {
        ...s.ham,
        display: isMobile ? 'flex' : 'none',
    };

    return (
        <div style={s.app}>
            {/* Sidebar scrollbar styling */}
            <style>{`
                .sidebar-nav::-webkit-scrollbar { width: 6px; }
                .sidebar-nav::-webkit-scrollbar-track { background: transparent; }
                .sidebar-nav::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.12); border-radius: 3px; }
                .sidebar-nav::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.25); }
                .sidebar-nav { scrollbar-width: thin; scrollbar-color: rgba(255,255,255,0.12) transparent; }
            `}</style>
            {/* Terms acceptance popup */}
            {!termsAccepted && <TermsAcceptancePopup onAccepted={() => setTermsAccepted(true)} />}
            {/* Trial credit popup */}
            {showTrialPopup && <TrialCreditPopup onClose={() => setShowTrialPopup(false)} />}

            {/* Mobile overlay */}
            <div style={overlayStyle} onClick={closeSidebar} />

            {/* Sidebar */}
            <div style={sidebarStyle}>
                {/* Logo */}
                <div style={s.logo}>
                    <img src="/logo.svg" alt="OrbitGrowth" style={{ height: 36, width: 'auto', borderRadius: 8 }} />
                </div>

                {/* Nav */}
                <nav className="sidebar-nav" style={s.nav}>
                    <NavLink to="/dashboard" onClick={closeSidebar} style={({ isActive }) => navStyle(isActive)}>
                        <span>📊</span> Dashboard
                    </NavLink>
                    <NavLink to="/orders" onClick={closeSidebar} style={({ isActive }) => navStyle(isActive)}>
                        <span>📋</span> My Orders
                    </NavLink>
                    {/* 2026-08-05: Subscriptions dismesse — link nav rimosso */}
                    <NavLink to="/new-order" onClick={closeSidebar} style={({ isActive }) => navStyle(isActive)}>
                        <span>➕</span> New Order
                    </NavLink>
                    <NavLink to="/tickets" onClick={closeSidebar} style={({ isActive }) => navStyle(isActive)}>
                        <span>🎫</span> Support
                    </NavLink>
                    <NavLink to="/wallet" onClick={closeSidebar} style={({ isActive }) => navStyle(isActive)}>
                        <span>💼</span> Wallet
                    </NavLink>
                    <NavLink to="/deposit" onClick={closeSidebar} style={({ isActive }) => navStyle(isActive)}>
                        <span>💳</span> Add Funds
                    </NavLink>
                    <NavLink to="/api-key" onClick={closeSidebar} style={({ isActive }) => navStyle(isActive)}>
                        <span>🔑</span> API Key
                    </NavLink>
                    <div style={s.section}>Premium</div>
                    <NavLink to="/zenith" onClick={closeSidebar} style={({ isActive }) => ({
                        ...navStyle(isActive),
                        background: isActive ? 'rgba(109,40,217,0.15)' : 'transparent',
                        borderLeft: isActive ? '3px solid #7c3aed' : '3px solid transparent',
                        color: isActive ? '#c4b5fd' : '#7c3aed',
                    })}>
                        <img src="/zenith-logo.svg" alt="Z" style={{ height: 28, width: 'auto', opacity: 0.9, filter: 'brightness(0) invert(0.6) sepia(1) hue-rotate(240deg)' }} />
                        <span style={{ color: '#7c3aed', fontWeight: 600 }}>Zenith Management</span>
                    </NavLink>

                    {isAdmin && (
                        <>
                            <div style={s.section}>Admin</div>
                            <NavLink to="/admin/clients"  onClick={closeSidebar} style={({ isActive }) => navStyle(isActive)}><span>👥</span> Clients</NavLink>
                            <NavLink to="/admin/services" onClick={closeSidebar} style={({ isActive }) => navStyle(isActive)}><span>⚙️</span> Services</NavLink>
                            <NavLink to="/admin/orders"   onClick={closeSidebar} style={({ isActive }) => navStyle(isActive)}><span>📦</span> All Orders</NavLink>
                            {/* 2026-08-05: Subscriptions dismesse — link admin rimosso */}
                            <NavLink to="/admin/payments" onClick={closeSidebar} style={({ isActive }) => navStyle(isActive)}><span>💰</span> Payments</NavLink>
                            <NavLink to="/admin/bonuses"        onClick={closeSidebar} style={({ isActive }) => navStyle(isActive)}><span>🎁</span> Bonuses</NavLink>
                            <NavLink to="/admin/tickets"       onClick={closeSidebar} style={({ isActive }) => navStyle(isActive)}><span>🎫</span> Tickets</NavLink>
                            <NavLink to="/admin/analytics"     onClick={closeSidebar} style={({ isActive }) => navStyle(isActive)}><span>📊</span> Analytics</NavLink>
                            <NavLink to="/admin/tiers"         onClick={closeSidebar} style={({ isActive }) => navStyle(isActive)}><span>👑</span> Tiers</NavLink>
                            <NavLink to="/admin/settings"      onClick={closeSidebar} style={({ isActive }) => navStyle(isActive)}><span>🛠️</span> Settings</NavLink>
                        </>
                    )}
                </nav>

                {/* Bottom */}
                <div style={s.bottom}>
                    <div style={s.username}>{client?.username || 'User'}</div>
                    <button style={s.logout} onClick={logout}>Logout</button>
                    <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ display: 'block', textAlign: 'center', fontSize: 11, color: '#475569', marginTop: 10, textDecoration: 'none' }}>
                        Terms of Service
                    </a>
                </div>
            </div>

            {/* Main content */}
            <div style={s.main}>
                <div style={{
                    ...s.header,
                    ...(location.pathname.startsWith('/admin') ? { background: '#0a0e1a', borderBottom: '1px solid rgba(255,255,255,0.08)', padding: '8px 16px', minHeight: 0 } : {}),
                }}>
                    <div style={s.hLeft}>
                        {/* Hamburger (mobile only) */}
                        <button style={hamStyle} onClick={() => setSidebarOpen(!sidebarOpen)} aria-label="Toggle menu">
                            <span style={{ ...s.hamLine, ...(location.pathname.startsWith('/admin') ? { background: '#94a3b8' } : {}) }} />
                            <span style={{ ...s.hamLine, ...(location.pathname.startsWith('/admin') ? { background: '#94a3b8' } : {}) }} />
                            <span style={{ ...s.hamLine, ...(location.pathname.startsWith('/admin') ? { background: '#94a3b8' } : {}) }} />
                        </button>
                        {!location.pathname.startsWith('/admin') && (
                            <img src="/logo.svg" alt="OrbitGrowth" style={{ height: 32, width: 'auto', borderRadius: 6 }} />
                        )}
                    </div>
                    {!location.pathname.startsWith('/admin') && (
                        <span style={s.hBal}>€{parseFloat(balance || 0).toFixed(2)}</span>
                    )}
                </div>
                <div className="og-content" ref={contentRef} style={{
                    ...s.content,
                    ...(location.pathname.startsWith('/admin') ? { padding: '16px 12px' } : {}),
                }}>
                    {showCsNotice && !location.pathname.startsWith('/admin') && (
                        <div style={{
                            background: '#fff8e1', color: '#8a6d00', border: '1px solid #ffe08a',
                            borderRadius: 10, padding: '12px 16px', marginBottom: 16,
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, fontSize: 14,
                        }}>
                            <span>⚠️ <b>Service temporarily paused.</b> We're working through a major Instagram update that's blocking action execution. All affected orders have been <b>fully refunded to your balance</b>. We'll be back shortly — thank you for your patience. 💙</span>
                            <span onClick={dismissCsNotice} style={{ cursor: 'pointer', fontWeight: 700, padding: '0 6px' }} aria-label="Dismiss">✕</span>
                        </div>
                    )}
                    <Outlet />
                </div>
                {/* Back to top button — shows after scrolling down */}
                {showBackToTop && (
                    <button
                        onClick={() => contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
                        style={{
                            position: 'fixed', bottom: 24, right: 24, zIndex: 50,
                            width: 44, height: 44, borderRadius: '50%',
                            background: 'linear-gradient(135deg, #1d4ed8, #06b6d4)',
                            color: '#fff', border: 'none', cursor: 'pointer',
                            boxShadow: '0 4px 16px rgba(29,78,216,0.4)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 20, fontWeight: 700,
                            transition: 'opacity 0.2s, transform 0.2s',
                        }}
                        aria-label="Back to top"
                    >↑</button>
                )}
            </div>
        </div>
    );
}
